import * as THREE from "three";
import { gsap } from "gsap";
import { isSP } from "../../../common/functions/isSP.es6";
import { CustomEase } from "gsap/CustomEase";
gsap.registerPlugin(CustomEase);

const pos_nrw = [
  {
    fromPosition: [0, -2.5, 0],
    position: [0.7, -1.9, 0],
    angle: [-10, -25, -20],
  },
  {
    fromPosition: [0.45, 0.7, 0],
    position: [-0.45, 1.7, 0],
    angle: [8, 25, 27],
  },
  {
    fromPosition: [0, -0.2, 0],
    position: [0.6, 0.65, 0],
    angle: [-20, -25, -30],
  },
  {
    fromPosition: [0.6, 0.2, 0],
    position: [-0.6, -0.2, 0],
    angle: [-10, 40, 40],
  },
];

const pos = [
  {
    fromPosition: [-1, 0, 0],
    position: [-2, 0.6, 0],
    angle: [0, 30, 25],
  },
  {
    fromPosition: [-1.2, -0.4, 0],
    position: [-0.6, -0.2, 0],
    angle: [-10, -30, -10],
  },
  {
    fromPosition: [1.4, -0.5, 0],
    position: [0.7, 0.5, 0],
    angle: [10, -40, 20],
  },
  {
    fromPosition: [1.1, -1.0, 0],
    position: [2.2, 0, 0],
    angle: [-20, -55, -45],
  },
];

export class Mesh {
  constructor(stage, index, count, meshLoadEvent) {
    this.meshLoadEvent = meshLoadEvent;
    this.meshLoadCount = count;
    this.index = index;
    this.stage = stage;
    this.geometryParam = {
      width: 1,
      height: 1,
      widthSegments: 2,
      heightSegments: 2,
    };
    this.scale = 0.6;
    this.position = null;
    this.fromPosition = null;
    this.angle = null;

    this.materialParam = {
      useWireframe: false,
    };

    this.texture1 = null;
    this.texture2 = null;
    this.geometry = null;
    this.material = null;
    this.mesh = null;
    this.card1 = null;
    this.card2 = null;
  }

  init() {
    this.setMesh();
    this.setClock();
  }

  setClock() {
    this.clock = new THREE.Clock();
  }

  setMesh() {
    const themeDir = "/wp/wp-content/themes/brotherash_official/";
    const loader = new THREE.TextureLoader();

    this.geometry = new THREE.PlaneGeometry(
      this.geometryParam.width,
      this.geometryParam.height,
      this.geometryParam.widthSegments,
      this.geometryParam.heightSegments
    );

    loader.load(
      `${themeDir}assets/img/top/mv/card/${this.index}-2d.webp`,
      (texture1) => {
        const texture2 = loader.load(
          `${themeDir}assets/img/top/mv/card/${this.index}-3d.webp`
        );

        this.texture1 = texture1;
        this.texture2 = texture2;

        texture1.colorSpace = THREE.SRGBColorSpace;
        texture2.colorSpace = THREE.SRGBColorSpace;

        this.material = new THREE.MeshLambertMaterial({
          map: texture1,
          transparent: true,
        });

        this.material2 = new THREE.MeshLambertMaterial({
          map: texture2,
          transparent: true,
        });

        const card1 = new THREE.Mesh(this.geometry, this.material);
        const card2 = new THREE.Mesh(this.geometry, this.material2);

        this.card1 = card1;
        this.card2 = card2;

        const group = new THREE.Object3D();
        group.add(card1);
        group.add(card2);

        card1.material.opacity = card2.material.opacity = 0;
        card2.rotation.y = (180 * Math.PI) / 180;

        this.mesh = group;
        this.stage.scene.add(group);
        this.setMeshPosition();
        this.setMeshScale();
        this.meshLoadCount.count++;

        if (this.meshLoadCount.count >= 4) {
          //console.log(this.meshLoadCount);
          //console.log("glLoaded");
          window.dispatchEvent(this.meshLoadEvent);
        }
      }
    );
  }

  setMeshScale() {
    const cardAspect = 890 / 590;
    this.card1.scale.x = 1 * this.scale;
    this.card1.scale.y = cardAspect * this.scale;
    this.card2.scale.x = 1 * this.scale;
    this.card2.scale.y = cardAspect * this.scale;
  }

  setMeshPosition() {
    const scale = isSP() ? 0.38 : 0.6;
    const devicePos = isSP() ? pos_nrw[this.index] : pos[this.index];

    this.scale = scale;

    this.position = new THREE.Vector3(
      devicePos.position[0] * this.scale,
      devicePos.position[1] * this.scale,
      devicePos.position[2] * this.scale
    );

    this.fromPosition = new THREE.Vector3(
      devicePos.fromPosition[0] * this.scale,
      devicePos.fromPosition[1] * this.scale,
      devicePos.fromPosition[2] * this.scale
    );

    this.angle = new THREE.Vector3(
      (devicePos.angle[0] * Math.PI) / 180,
      (devicePos.angle[1] * Math.PI) / 180,
      (devicePos.angle[2] * Math.PI) / 180
    );

    //console.log(devicePos, this.position, this.index, this.scale);
    this.mesh.position.set(this.position.x, this.position.y, this.position.z);
    this.mesh.rotation.set(this.angle.x, this.angle.y, this.angle.z);
  }

  onResize() {
    this.setMeshPosition();
    this.setMeshScale();
  }

  onRaf(elapsed) {
    if (this.card1) {
      this.card1.position.y = Math.sin(elapsed * 0.5) * 0.02;
    }
    if (this.card2) {
      this.card2.position.y = Math.sin(elapsed * 0.5) * 0.02;
    }

    //Reset Rotation
    if (elapsed === 0) {
      //console.log("raf start");
      this.resetCardRotation();
    }
  }

  resetCardRotation() {
    if (this.card1) {
      this.card1.rotation.y = 0;
    }
    if (this.card2) {
      this.card2.rotation.y = (180 * Math.PI) / 180;
    }
  }

  inAnim(delay = 0) {
    gsap.fromTo(
      this.mesh.position,
      {
        x: this.fromPosition.x,
        y: this.fromPosition.y,
      },
      {
        x: this.position.x,
        y: this.position.y,
        duration: 1,
        delay: delay,
        ease: "circ.inOut",
      }
    );

    gsap.to(this.card1.material, {
      opacity: 1,
      duration: 0.5,
      delay: delay + 0.5,
    });

    gsap.to(this.card2.material, {
      opacity: 1,
      duration: 0.5,
      delay: delay + 0.5,
    });

    gsap.fromTo(
      this.card1.rotation,
      { y: (90 * Math.PI) / 180 },
      {
        y: 0,
        duration: 1,
        delay: delay,
        ease: "circ.inOut",
      }
    );
  }

  turn(delay = 0, mode, _duration = 1) {
    if (mode === "frontToBack") {
      gsap.to(this.card1.rotation, {
        y: (180 * Math.PI) / 180,
        duration: _duration,
        delay: delay,
        ease: "circ.inOut",
        onComplete: () => {
          this.card1.rotation.y = (-180 * Math.PI) / 180;
        },
      });

      gsap.to(this.card2.rotation, {
        y: (360 * Math.PI) / 180,
        duration: _duration,
        delay: delay,
        ease: "circ.inOut",
        onComplete: () => {
          this.card2.rotation.y = (0 * Math.PI) / 180;
        },
      });
    } else {
      gsap.to(this.card1.rotation, {
        y: (0 * Math.PI) / 180,
        duration: _duration,
        delay: delay,
        ease: "circ.inOut",
      });

      gsap.to(this.card2.rotation, {
        y: (180 * Math.PI) / 180,
        duration: _duration,
        delay: delay,
        ease: "circ.inOut",
      });
    }
  }

  destroy() {
    this?.stage?.scene?.remove(this.mesh);
    this?.geometry?.dispose();
    this?.material?.dispose();
    this?.mesh?.material?.dispose();
    this?.mesh?.geometry?.dispose();
    this?.texture?.dispose();
    //console.log("Mesh destroy");
  }
}
